/**
	* Raster image tool
	* Handles: image rendering onto map
	* @module SelectTool
*/

/// Imports

// React
import React, {useState, useEffect} from "react";

// OpenLayers
import OlMap from "ol/Map";
import Transform from "lib/includes/ol-ext/interaction/Transform";
import Feature from "ol/Feature";
import Geometry from "ol/geom/Geometry";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import BaseLayer from "ol/layer/Base";
import Group from "ol/layer/Group";
import Collection from "ol/Collection";
import Polygon, { fromExtent } from "ol/geom/Polygon";
import GeoJSON from "ol/format/GeoJSON";
import { GeoJSONFeature } from "ol/format/GeoJSON";
import {
  never,
  platformModifierKeyOnly,
  primaryAction,
} from "ol/events/condition.js";
import { getCenter } from "ol/extent"

// Lib
import MapToolMode from "lib/enums/maptoolmode";
import RasterImage from "lib/classes/rasterimage";
import OpusLayerGroup from "lib/classes/opuslayergroup";
import OpusLayer from "lib/classes/opuslayer";
import { assetURL } from "lib/utilities/asset";
import { removeFromArray } from "lib/utilities/misc";
import { 
	getExtentFromPixels,
	getIsRasterFeatureOnMap,
	getVisibleLayerCodes
} from "lib/utilities/map";
import { 
	calculateCenter, 
	formatFeature,
	getSourceFeaturesJson
} from "lib/utilities/feature";

// Lang
import { getLocaleString } from "lib/localization/vendor";

// Styles
import StyleFeature from "styles/script/stylefeature";
import "styles/css/RasterImageTool.scss";

interface RasterImageToolProps {
	olMap:OlMap,
	currentToolMode:MapToolMode
}

/*
	Raster feature layer & source
*/
const rasterFeatureSource:VectorSource = new VectorSource();
const rasterFeatureLayer:VectorLayer<VectorSource> = new VectorLayer({
	source: rasterFeatureSource,
	zIndex: 3,
	style: StyleFeature
});

/*
	ol-ext transform interaction
*/
const transform:Transform = new Transform({
	layers: [rasterFeatureLayer],
	keepAspectRatio: (e:any) => true,
	stretch: false,
	selection: false
});

/*
	Legend opus layer for the raster images
*/
var legendLayer:OpusLayer;

/*
	Actual static image layer group
*/
const staticImageLayerGroup:Group = new Group();
const staticImageLayers:Collection<BaseLayer> = staticImageLayerGroup.getLayers();

export default function RasterImageTool({olMap, currentToolMode}:RasterImageToolProps) {
	const mapConfig:any = olMap.get("opus-config");
	const localStorageId:string = `omv-ri-${mapConfig.id}`;
	
	// Set the raster feature layer to the map as a property (for SelectTool to reference)
	if(!olMap.get("raster-feature-layer"))
		olMap.set("raster-feature-layer", rasterFeatureLayer);
	
	const [locked, setLocked] = useState<boolean>(true);
	const [selectedFeatures, setSelectedFeatures] = useState<Feature<Geometry>[]>([]);
	const [legendLayerVisible, setLegendLayerVisible] = useState<boolean>(false);
	
	// When map config changes (when locale changes) (must go first)
	useEffect(() => {
		setupLegendLayer();
	}, [mapConfig]);
	
	// When legend layer visibility changes
	useEffect(() => toggleLegendLayer(legendLayerVisible), [legendLayerVisible]);
	
	// Initialize
	useEffect(() => {
		// Add raster feature layer and static image layer group to map
		olMap.addLayer(rasterFeatureLayer);
		olMap.addLayer(staticImageLayerGroup);
		
		setupTransform();
		
		/* 
			Annoyingly this has to be done after a delay or the auto showing of the legend layer fails. Its due to an anomaly where the 'opus-layer-groups' property on olMap doesn't reflect changes to the visible property on each layer within the layer groups during initialization of all the modules.
		*/
		setTimeout(() => loadLocalFeatures(), 100); 
		
		_events.on("raster-image-uploaded", (url:string, image:any) => {
			// Deselect all features to avoid confusion
			_events.invoke("deselect-all-features");
			
			/*
				Ensure the image doesn't spawn larger than the screen
			*/
			let width:number = image.width;
			let height:number = image.height;
			
			const maxWidth:number = _dimensions.width * 0.8;
			const maxHeight:number = _dimensions.height * 0.8;
			
			if(width > maxWidth || height > maxHeight) {
				if(width > maxWidth) {
					height = Math.round((height * maxWidth) / width);
					width = maxWidth;
				}
				if(height > maxHeight) {
					width = Math.round((width * maxHeight) / height);
					height = maxHeight;
				}
			}
			
			const extent:Array<number> = getExtentFromPixels(olMap, width, height, mapConfig.epsgCodeView);
			
			// Set mode to select tool
			_ui.setToolMode(MapToolMode.Select);
			
			// Timeout needed so the tool mode can change before we add the raster & its feature and focus on it
			setTimeout(() => {
				const rasterFeature:Feature<Geometry> = createRasterImage(url, extent);
				
				// Auto select new uploaded raster feature
				_events.invoke("select-features", [rasterFeature]);	
				
				setLocked(false);
				sync();
				onRastersAddedOrRemoved();
			}, 100);
		});
		_events.on("delete-raster-img", (feature:Feature<Geometry>) => {
			deleteRasterImage(feature);
			sync();
		});
		_events.on("selected-features", (features:Feature<Geometry>[]) => {
			(transform as any).setSelection([]);
			
			setSelectedFeatures(features);
			
			features.forEach((feature:Feature<Geometry>) => {
				if(!feature.get("rasterimg"))
					return;
				
				transform.select(feature, true);
			});
		});
		
		const layerUpdate = () => {
			// When static image opus layer changed visibility
			const codes:string[] = getVisibleLayerCodes(olMap);
			const vis:boolean = codes.indexOf(_config.rastersLegendId) > -1;
			
			rasterFeatureLayer.setVisible(vis);
			staticImageLayerGroup.setVisible(vis);
			setLegendLayerVisible(vis);
		}
		
		_events.on("layer-toggled", layerUpdate);
		_events.on("layer-update", layerUpdate);
		_events.on("sync-layers", layerUpdate);
		
		_events.on("raster-transform", onTransform);
		
		layerUpdate();
	}, []);
	
	// When lock state changes
	useEffect(() => {
		// Deselect all features if locked
		locked && deselectAllFeatures();
		
		transform.setActive(!locked);
		
		rasterFeatureLayer.set("locked", locked);
		rasterFeatureLayer.changed();
		
		olMap.set("ri-locked", locked);
	}, [locked]);
	
	const createRasterImage = (url:string, extent:Array<number>, rotation:number = 0, opacity:number = 0.5):Feature<Geometry> => {
		const unrotatedExtent:Array<number> = [...extent]; // Extent is always unrotated
		
		const rasterImage:RasterImage = new RasterImage({
			url: url,
			epsgCodeView: mapConfig.epsgCodeView,
			extent: extent,
			rasterFeatureLayer: rasterFeatureLayer,
			opacity: Number(opacity || 0.5)
		});
		const rasterFeature:Feature<Geometry> = new Feature<Geometry>({
			rasterimg: rasterImage,
			opacity: rasterImage.opacity,
			geometry: fromExtent([0,0,0,0])
		});
		
		// Update raster feature geometry to match the image
		rasterFeature.setGeometry(fromExtent(rasterImage.extent));
		
		// Add RI image layer to the map
		staticImageLayers.push(rasterImage.layer);
		
		// Add raster feature to raster source
		rasterFeatureSource.addFeature(rasterFeature);
		
		// Rotate according to param
		rasterFeature.getGeometry().rotate(rotation, getCenter(extent));
		rasterImage.rotate(rotation);
		rasterImage.source.set("unrotatedExtent", unrotatedExtent); // this extent should always be unrotated
		rasterImage.setExtent(rasterFeature.getGeometry().getExtent());
		
		// Pick up on opacity change
		rasterFeature.on("propertychange", (e:any) => {
			if(e.key != "opacity") 
				return;
				
			rasterImage.setOpacity(rasterFeature.get("opacity"));
			sync();
		});
		
		return rasterFeature;
	}
	const deleteRasterImage = (feature:Feature<Geometry>) => {
		const rasterImage:RasterImage = feature.get("rasterimg");
		
		if(!rasterImage)
			return;
		
		// Remove raster image layer
		staticImageLayers.remove(rasterImage.layer);
		
		// Remove feature from source
		rasterFeatureSource.removeFeature(feature);
		
		// De-select all features to clear out the old geometry of the raster feature from it
		deselectAllFeatures();
		
		onRastersAddedOrRemoved();
	}
	const loadLocalFeatures = () => {
		const jsonData:string = localStorage.getItem(localStorageId);
		
		if(!jsonData) {
			console.log("No saved feature data: " + jsonData);
			return;
		}
		
		let geoJsonFeatures:Collection<GeoJSONFeature> = new Collection<GeoJSONFeature>();
		
		try {
			geoJsonFeatures = JSON.parse(jsonData);
		} catch(e:any) {
			console.log("Corrupt saved feature data. Removing it.")
			localStorage.removeItem(localStorageId);
			return;
		}
		
		addFeatures(geoJsonFeatures);
	}
	const addFeatures = (geoJsonFeatures:Collection<GeoJSONFeature>) => {
		const features:Feature<Geometry>[] = new GeoJSON({
			featureProjection: mapConfig.epsgCodeView 
		}).readFeatures(geoJsonFeatures);
		
		features.forEach((feature:Feature<Geometry>) => {
			const rasterImageInfo:any = feature.get("rasterimg-info");
			
			if(!rasterImageInfo)
				return;
			
			createRasterImage(
				rasterImageInfo.url, 
				rasterImageInfo.extent, 
				rasterImageInfo.rotation,
				feature.get("opacity")
			);
		});
		
		if(features.length > 0)
			onRastersAddedOrRemoved();
	}
	const sync = () => {
		// Sync raster features
		try {
			localStorage.setItem(localStorageId, getSourceFeaturesJson(
				rasterFeatureSource,
				mapConfig.epsgCodeView,
				(features:Feature<Geometry>[]):Function[] => {
					const restoreMethods:Function[] = [];
					
					features.forEach((feature:Feature<Geometry>) => {
						if(feature.get("rasterimg")) {
							let ri:any = feature.get("rasterimg");
							
							feature.set("rasterimg-info", {
								url: ri.url,
								extent: ri.source.get("unrotatedExtent") ?? ri.extent,
								rotation: ri.rotation
							});
							
							feature.unset("rasterimg");
							
							restoreMethods.push(() => feature.set("rasterimg", ri));
						}
					});
					
					return restoreMethods;
				}
			));
		}catch(e:any) {
			_ui.showNotice('Image data could not be saved as it is too large.');
			localStorage.clear();
		}
	}
	
	const setupTransform = () => {
		transform.on(["rotating", "scaling", "translating"] as any, (e:any) => {
			e.features.forEach((feature:Feature<Geometry>) => {
				onTransform(feature, e);
			});
		});
		
		// Sync to local storage when transformation ends
		transform.on(["rotateend", "scaleend", "translateend"] as any, (e:any) => sync());
		
		olMap.addInteraction(transform);
	}
	const onTransform = (feature:Feature<Geometry>, e:any, shouldSync:boolean = false) => {
		const rasterImage:RasterImage = feature.get("rasterimg");
		
		if(!rasterImage)
			return;
		
		if(e.angleDelta)
			rasterImage.rotate(e.angleDelta);
			
		const unrotatedGeometry:Geometry = feature.getGeometry().clone();
		
		unrotatedGeometry.rotate(-rasterImage.rotation, getCenter(unrotatedGeometry.getExtent()));
		
		rasterImage.source.set("unrotatedExtent", unrotatedGeometry.getExtent());
		
		rasterImage.setExtent(feature.getGeometry().getExtent());
		
		if(shouldSync)
			sync();
	}
	const deselectAllFeatures = () => {
		_events.invoke("deselect-all-features");
	}
	const toggleLock = () => {
		setLocked(!locked);
		
		_ui.tooltip.hide();
		_ui.tooltip.rebuild();
	}
	const toggleLegendLayer = (visible:boolean) => {
		_events.invoke("toggle-layer-code", _config.rastersLegendId, visible);
	}
	const toggleLegendLayerListing = (hidden:boolean) => {
		_events.invoke("hide-layer", legendLayer, hidden);
	}
	const setupLegendLayer = () => {
		const layerGroups:OpusLayerGroup[] = olMap.get("opus-layer-groups");
		
		legendLayer = null;
		
		layerGroups.forEach((layerGroup:OpusLayerGroup) => {
			layerGroup.layers.forEach((layer:OpusLayer) => {
				if(layer.id == _config.rastersLegendId)
					legendLayer = layer;
			})
		});
		
		if(!legendLayer)
			throw new Error("No raster legend layer found. There must be one.");
			
		// Just do it ok (called before the raster features are loaded from local storage)
		onRastersAddedOrRemoved();
	}
	const onRastersAddedOrRemoved = () => {
		const isRasterOnMap:boolean = getIsRasterFeatureOnMap(olMap);
		
		toggleLegendLayerListing(!isRasterOnMap); // Show/hide legend layer group if present or not
		setLegendLayerVisible(isRasterOnMap); // Show/hide layer depending on if present
	}
	
	const drawToolButtons = () => {
		if(!getIsRasterFeatureOnMap(olMap) || !legendLayerVisible)
			return;
			
		const anySelected:boolean = selectedFeatures.length > 0;
		
		let extraClass = "";
		
		if(anySelected) {
			extraClass = "drawing-option--lock--fs";
			
			if(_ui.get("selectTool_variationsButtonShown"))
				extraClass = "drawing-option--lock--fs2";
		}
		
		return (
			<div 
				className={`drawing-option drawing-option--lock ${extraClass}`} 
				onClick={() => toggleLock()} 
				data-delay-show="800"
				data-tip={`${!locked ? "Lock" : "Unlock"} rasters`}
			>
				<img src={assetURL(`assets/images/${locked ? "locked" : "unlocked"}.png`)} />
			</div>	
		)
	}
	
	return (
		<>
			{currentToolMode == MapToolMode.Select && drawToolButtons()}
		</>
	)
}